import React, { type ComponentProps } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, type StyleProp, type TextStyle, View, type ViewStyle } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Button, DisplayText, useResponsive, VStack } from '@garnish/core';

import { useA11ySelector } from '@sg/a11y-helpers';

import { LoyaltyHomeChallengesRewardsContainer } from '../LoyaltyHomeChallengesRewardsContainer';

export const LoyaltyHomeFAQ = (props: LoyaltyHomeFaqProps) => {
  const { palette, title, subtitle, children, showHowItWorksModal, onForgotToScan, style } = props;

  const a11ySelector = useA11ySelector('loyalty.faq.container');

  const { match } = useResponsive();
  const containerStyle = match([undefined, styles.containerSM]);
  const headerStyle = match([styles.headerXS, styles.headerSM]);
  const flexOnSM = match([undefined, styles.flexContent]);

  return (
    <LoyaltyHomeChallengesRewardsContainer
      style={[containerPallettes[palette], containerStyle, style]}
      {...a11ySelector.props}
    >
      <View style={match([styles.contentXS, styles.contentSM])}>
        <View style={[headerStyle, flexOnSM]}>
          <DisplayText style={textPalettes[palette]} sizeMatch={['32', '40']}>
            {title}
          </DisplayText>

          <BodyText style={textPalettes[palette]} sizeMatch={['16', '18']}>
            {subtitle}
          </BodyText>
        </View>

        <VStack style={flexOnSM} hasDivider gap={0}>
          {children}
        </VStack>
      </View>

      <View style={styles.footerXS}>
        {showHowItWorksModal ? (
          <Button
            style={styles.button}
            size={match(['large', 'large-wide'])}
            palette={buttonPalettes[palette]}
            onPress={showHowItWorksModal}
          >
            <FormattedMessage {...messages.howItWorks} />
          </Button>
        ) : null}

        {onForgotToScan ? (
          <Button
            style={styles.button}
            size={match(['large', 'large-wide'])}
            palette={buttonPalettes[palette]}
            onPress={onForgotToScan}
          >
            <FormattedMessage {...messages.forgotToScan} />
          </Button>
        ) : null}
      </View>
    </LoyaltyHomeChallengesRewardsContainer>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  howItWorks: {
    defaultMessage: 'How it works',
    description: 'Loyalty | Home | FAQ | How it works button',
  },
  forgotToScan: {
    defaultMessage: 'Forgot to scan?',
    description: 'Loyalty | Home | FAQ | Forgot to scan button',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerSM: {
    paddingVertical: theme.spacing['16'],
  },
  contentXS: {
    gap: theme.spacing['6'],
  },
  contentSM: {
    flexDirection: 'row',
    gap: theme.spacing['10'],
  },
  button: {
    minWidth: 160,
    alignSelf: 'center',
  },
  flexContent: {
    flex: 1,
  },
  kaleText: {
    color: theme.colors.KALE,
  },
  whiteText: {
    color: theme.colors.WHITE,
  },
  headerXS: {
    gap: theme.spacing['4'],
  },
  headerSM: {
    paddingVertical: theme.spacing['6'],
    gap: theme.spacing['4'],
  },
  footerXS: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing['4'],
  },
  lightBackground: {
    backgroundColor: theme.colors.CUCUMBER,
  },
  darkBackground: {
    backgroundColor: theme.colors.BLACK,
    borderColor: theme.colors.WHITE,
    borderTopWidth: 1,
    borderBottomWidth: 1,
  },
});

// ─── Palettes ───────────────────────────────────────────────────────────────

const containerPallettes: Record<LoyaltyHomeFaqProps['palette'], ViewStyle | undefined> = {
  light: styles.lightBackground,
  dark: styles.darkBackground,
};

const textPalettes: Record<LoyaltyHomeFaqProps['palette'], TextStyle> = {
  light: styles.kaleText,
  dark: styles.whiteText,
};

const buttonPalettes: Record<
  LoyaltyHomeFaqProps['palette'],
  ComponentProps<typeof Button>['palette']
> = {
  light: 'kale',
  dark: 'cream',
};

// ─── Types ──────────────────────────────────────────────────────────────────

type LoyaltyHomeFaqProps = {
  palette: 'light' | 'dark';
  title: string;
  subtitle: string;
  children: React.ReactNode;
  showHowItWorksModal?: () => void;
  onForgotToScan?: () => void;
  style?: StyleProp<ViewStyle>;
};
